import { useState } from 'react';
import axios from 'axios';
import { FolderArchive, FileText, Award, Euro, Eye, Download, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { openPdfBlob } from '../../utils/pdfOpener';

const DossierCandidatSection = ({ stagiaireId, stagiaire, dossierCandidat }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!dossierCandidat) return null;

  const nomFichierBase = () => {
    const n = (stagiaire?.user_info?.nom || '').replace(/\s+/g, '_');
    const p = (stagiaire?.user_info?.prenom || '').replace(/\s+/g, '_');
    return `${n}_${p}`.replace(/^_|_$/g, '') || 'Stagiaire';
  };

  const fetchAndHandle = async (endpoint, filename, mode, errorMsg) => {
    try {
      const res = await axios.get(endpoint, { responseType: 'blob' });
      openPdfBlob(res.data, filename, mode);
    } catch (e) {
      toast.error(errorMsg);
    }
  };

  return (
    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg shadow-sm overflow-hidden" data-testid="dossier-candidat-section">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-amber-100/50 transition-colors"
        data-testid="toggle-dossier-candidat"
      >
        <div className="flex items-center gap-3">
          <FolderArchive className="h-5 w-5 text-amber-600" />
          <span className="text-xl font-display font-semibold text-amber-800">
            Dossier Candidat (Historique)
          </span>
          {dossierCandidat.converted_from_candidat && (
            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-xs font-medium">Converti</span>
          )}
        </div>
        <svg className={`h-5 w-5 text-amber-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="border-t border-amber-200 p-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dossier-dates">
            {dossierCandidat.date_inscription && (
              <DateCard label="Date d'inscription" date={dossierCandidat.date_inscription} />
            )}
            {dossierCandidat.date_acceptation && (
              <DateCard label="Date d'acceptation" date={dossierCandidat.date_acceptation} />
            )}
            {dossierCandidat.converted_at && (
              <DateCard label="Date de conversion" date={dossierCandidat.converted_at} />
            )}
            {dossierCandidat.centre && (
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <p className="text-xs text-slate-500 mb-1">Centre d'inscription</p>
                <p className="text-sm font-medium text-slate-800 capitalize">{dossierCandidat.centre}</p>
              </div>
            )}
          </div>

          {/* Fiche d'inscription */}
          <div className="bg-white rounded-lg p-4 border border-amber-100" data-testid="dossier-fiche-inscription">
            <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Fiche d'inscription
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => fetchAndHandle(
                  `/candidats/${stagiaireId}/fiche-inscription/pdf`,
                  `${nomFichierBase()}_Fiche_Inscription.pdf`,
                  'view',
                  "Impossible de charger la fiche d'inscription"
                )}
                className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm hover:bg-amber-200 flex items-center gap-2"
                data-testid="dossier-fiche-inscription-btn"
              >
                <Eye className="h-4 w-4" /> Voir
              </button>
              <button
                onClick={() => fetchAndHandle(
                  `/candidats/${stagiaireId}/fiche-inscription/pdf`,
                  `${nomFichierBase()}_Fiche_Inscription.pdf`,
                  'download',
                  "Impossible de télécharger la fiche d'inscription"
                )}
                className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 flex items-center gap-2"
                data-testid="dossier-fiche-inscription-dl-btn"
              >
                <Download className="h-4 w-4" /> Télécharger
              </button>
            </div>
          </div>

          {dossierCandidat.tests_selection && (
            <div className="bg-white rounded-lg p-4 border border-amber-100" data-testid="dossier-tests">
              <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
                <Award className="h-4 w-4" /> Résultats des Tests de Sélection
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(dossierCandidat.tests_selection).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className={`font-medium px-2 py-0.5 rounded text-xs ${
                      value === true || value === 'reussi' || value === 'apte' ? 'bg-green-100 text-green-700' :
                      value === false || value === 'echoue' || value === 'inapte' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {typeof value === 'boolean' ? (value ? 'Réussi' : 'Échoué') : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dossierCandidat.pif_data && (
            <PifCard stagiaireId={stagiaireId} nomBase={nomFichierBase()} pifData={dossierCandidat.pif_data} fetchAndHandle={fetchAndHandle} />
          )}

          {dossierCandidat.paiements && dossierCandidat.paiements.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-amber-100" data-testid="dossier-paiements">
              <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
                <Euro className="h-4 w-4" /> Paiements Candidat
              </h3>
              <div className="space-y-2">
                {dossierCandidat.paiements.map((p) => (
                  <div key={p.id || `${p.type}-${p.date}-${p.montant}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div>
                      <span className="text-slate-700 font-medium">{p.description || p.type || 'Paiement'}</span>
                      {p.date && <span className="text-xs text-slate-400 ml-2">{new Date(p.date).toLocaleDateString('fr-FR')}</span>}
                    </div>
                    <span className="font-semibold text-green-700">{p.montant ? `${p.montant} €` : '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {dossierCandidat.documents_candidat && dossierCandidat.documents_candidat.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-amber-100" data-testid="dossier-documents">
              <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Documents uploadés lors de la candidature ({dossierCandidat.documents_candidat.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {dossierCandidat.documents_candidat.map((doc) => (
                  <div key={doc.document_id || doc.nom_fichier || doc.type_document} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-amber-500 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-slate-700 truncate block">{doc.type_document || doc.nom || doc.filename || 'Document'}</span>
                        {doc.nom_fichier && <span className="text-xs text-slate-400 truncate block">{doc.nom_fichier}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {doc.statut && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full mr-1 ${
                          doc.statut === 'accepte' ? 'bg-green-100 text-green-700' :
                          doc.statut === 'refuse' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {doc.statut === 'accepte' ? 'OK' : doc.statut === 'refuse' ? 'Refusé' : 'En attente'}
                        </span>
                      )}
                      {doc.document_id && (
                        <>
                          <button
                            onClick={() => fetchAndHandle(
                              `/candidats/doc-view/${doc.document_id}`,
                              doc.nom_fichier || doc.type_document || 'document.pdf',
                              'view',
                              'Impossible de charger le document'
                            )}
                            className="p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded"
                            title="Voir"
                            data-testid={`doc-view-${doc.document_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => fetchAndHandle(
                              `/candidats/doc-view/${doc.document_id}`,
                              doc.nom_fichier || doc.type_document || 'document.pdf',
                              'download',
                              'Impossible de télécharger'
                            )}
                            className="p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded"
                            title="Télécharger"
                            data-testid={`doc-dl-${doc.document_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DateCard = ({ label, date }) => (
  <div className="bg-white rounded-lg p-3 border border-amber-100">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className="text-sm font-medium text-slate-800">{new Date(date).toLocaleDateString('fr-FR')}</p>
  </div>
);

const PifCard = ({ stagiaireId, nomBase, pifData, fetchAndHandle }) => (
  <div className="bg-white rounded-lg p-4 border border-amber-100" data-testid="dossier-pif">
    <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
      <FileText className="h-4 w-4" /> Programme Individuel de Formation (PIF)
    </h3>
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="text-sm">
        <p className="text-slate-600">
          Statut : <span className={`font-medium ${pifData.statut === 'signe' ? 'text-green-600' : 'text-orange-600'}`}>
            {pifData.statut === 'signe' ? 'Signé' : pifData.statut === 'en_attente' ? 'En attente' : pifData.statut || 'Brouillon'}
          </span>
        </p>
        {pifData.date_signature && <p className="text-xs text-slate-400 mt-1">Signé le {pifData.date_signature}</p>}
        {pifData.archive_date && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <Shield className="h-3 w-3" /> Archivé le {new Date(pifData.archive_date).toLocaleDateString('fr-FR')} à {new Date(pifData.archive_date).toLocaleTimeString('fr-FR')}
          </p>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => fetchAndHandle(
            `/candidats/${stagiaireId}/pif/pdf`,
            `${nomBase}_PIF.pdf`,
            'view',
            'Impossible de charger le PIF'
          )}
          className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm hover:bg-amber-200 flex items-center gap-2"
          data-testid="dossier-pif-view-btn"
        >
          <Eye className="h-4 w-4" /> Voir
        </button>
        <button
          onClick={() => fetchAndHandle(
            `/candidats/${stagiaireId}/pif/pdf`,
            `${nomBase}_PIF.pdf`,
            'download',
            'Impossible de télécharger le PIF'
          )}
          className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 flex items-center gap-2"
          data-testid="dossier-pif-dl-btn"
        >
          <Download className="h-4 w-4" /> Télécharger
        </button>
        {pifData.archive_date && (
          <button
            onClick={() => fetchAndHandle(
              `/candidats/${stagiaireId}/pif/archive`,
              `${nomBase}_PIF_archive.pdf`,
              'download',
              'Archive non disponible'
            )}
            className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex items-center gap-2"
            data-testid="dossier-pif-archive-btn"
          >
            <Shield className="h-3.5 w-3.5" /> PDF Archivé
          </button>
        )}
      </div>
    </div>
  </div>
);

export default DossierCandidatSection;
